// src/pages/Orders/OrderForm/OrderFormSections/EnhancedPricingSummarySection.jsx
import React, { useState } from "react";
import Button from "../../../../components/common/Button";
import Badge from "../../../../components/common/Badge";
import {
  FiDollarSign,
  FiRefreshCw,
  FiChevronDown,
  FiChevronUp,
  FiInfo,
  FiAlertCircle,
  FiLock,
  FiClock,
  FiPercent,
  FiTag,
  FiCheck,
} from "react-icons/fi";

const EnhancedPricingSummarySection = ({
  pricingSummary,
  calculating,
  onRecalculate,
  sessionPricing,
  priceAdjustments = [],
  priceLock,
}) => {
  const [expandedSections, setExpandedSections] = useState({
    items: true,
    adjustments: true,
    exchange: true,
  });

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      day: "2-digit",
      month: "short",
    });
  };

  // Use session pricing if available, otherwise use calculated pricing
  const pricing = sessionPricing || pricingSummary;

  if (!pricing && !calculating) {
    return null;
  }

  const hasAdjustments = priceAdjustments && priceAdjustments.length > 0;

  // 🔥 FIX: Show exchange section if there are ANY exchange items, not just when totalExchangeValue > 0
  const hasExchange =
    pricing?.exchangeItems && pricing.exchangeItems.length > 0;

  // Calculate pending exchange value
  const pendingExchangeValue =
    pricing?.exchangeItems
      ?.filter((item) => !item.customerAccepted)
      .reduce((sum, item) => sum + (item.finalValue || 0), 0) || 0;

  const isLocked = priceLock?.isLocked;

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="p-4 bg-gradient-to-r from-primary to-primary-dark">
        <div className="flex justify-between items-center text-white">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <FiDollarSign className="text-xl" />
              <h3 className="text-lg font-semibold">Pricing Summary</h3>
            </div>
            {isLocked && (
              <div className="flex items-center gap-1 text-xs bg-white bg-opacity-20 px-2 py-1 rounded">
                <FiLock className="w-3 h-3" />
                <span>Prices Locked</span>
              </div>
            )}
          </div>

          <div className="text-right">
            <p className="text-sm opacity-90">Total Amount</p>
            <p className="text-2xl font-bold">
              {formatCurrency(pricing?.grandTotal || 0)}
            </p>
          </div>
        </div>
      </div>

      {calculating ? (
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-3"></div>
          <p className="text-gray-600">Calculating pricing...</p>
        </div>
      ) : pricing ? (
        <div className="p-4 space-y-4">
          {/* Price Lock Info */}
          {isLocked && priceLock && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start gap-2">
                <FiAlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-yellow-900 text-sm">
                    Prices Locked from Consultation
                  </p>
                  <p className="text-xs text-yellow-700 mt-1">
                    Locked by {priceLock.lockedBy} • Expires:{" "}
                    {formatDate(priceLock.lockExpiresAt)}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* 🔥 NEW: Pending Exchange Items Warning */}
          {pendingExchangeValue > 0 && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start gap-2">
                <FiAlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-yellow-900 text-sm">
                    Pending Exchange Items
                  </p>
                  <p className="text-xs text-yellow-700 mt-1">
                    {
                      pricing.exchangeItems.filter((i) => !i.customerAccepted)
                        .length
                    }{" "}
                    exchange item(s) worth{" "}
                    {formatCurrency(pendingExchangeValue)} awaiting customer
                    acceptance. These will be deducted only after acceptance.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Items Breakdown */}
          {pricing.items && pricing.items.length > 0 && (
            <div className="border border-gray-200 rounded-lg">
              <button
                type="button"
                onClick={() => toggleSection("items")}
                className="w-full px-4 py-3 flex justify-between items-center hover:bg-gray-50"
              >
                <div className="flex items-center gap-2">
                  <FiTag className="text-gray-600" />
                  <span className="font-medium">
                    Items ({pricing.items.length})
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">
                    {formatCurrency(pricing.subtotal)}
                  </span>
                  {expandedSections.items ? (
                    <FiChevronUp className="text-gray-400" />
                  ) : (
                    <FiChevronDown className="text-gray-400" />
                  )}
                </div>
              </button>

              {expandedSections.items && (
                <div className="border-t border-gray-200 p-4 space-y-3">
                  {pricing.items.map((item, idx) => (
                    <ItemPricingRow key={idx} item={item} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Price Adjustments */}
          {hasAdjustments && (
            <div className="border border-gray-200 rounded-lg">
              <button
                type="button"
                onClick={() => toggleSection("adjustments")}
                className="w-full px-4 py-3 flex justify-between items-center hover:bg-gray-50"
              >
                <div className="flex items-center gap-2">
                  <FiPercent className="text-green-600" />
                  <span className="font-medium">
                    Adjustments ({priceAdjustments.length})
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-green-600">
                    -{formatCurrency(pricing.totalAdjustments || 0)}
                  </span>
                  {expandedSections.adjustments ? (
                    <FiChevronUp className="text-gray-400" />
                  ) : (
                    <FiChevronDown className="text-gray-400" />
                  )}
                </div>
              </button>

              {expandedSections.adjustments && (
                <div className="border-t border-gray-200 p-4 space-y-2">
                  {priceAdjustments.map((adj, idx) => (
                    <AdjustmentRow key={idx} adjustment={adj} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* 🔥 FIXED: Exchange Items Section */}
          {hasExchange && (
            <div className="border border-gray-200 rounded-lg">
              <button
                type="button"
                onClick={() => toggleSection("exchange")}
                className="w-full px-4 py-3 flex justify-between items-center hover:bg-gray-50"
              >
                <div className="flex items-center gap-2">
                  <FiRefreshCw className="text-purple-600" />
                  <span className="font-medium">
                    Exchange Items ({pricing.exchangeItems.length})
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {/* 🔥 FIX: Show accepted count and value */}
                  <span className="text-sm text-purple-600">
                    {
                      pricing.exchangeItems.filter((i) => i.customerAccepted)
                        .length
                    }{" "}
                    accepted
                    {(pricing.totalExchangeValue || 0) > 0 && (
                      <> • -{formatCurrency(pricing.totalExchangeValue)}</>
                    )}
                  </span>
                  {expandedSections.exchange ? (
                    <FiChevronUp className="text-gray-400" />
                  ) : (
                    <FiChevronDown className="text-gray-400" />
                  )}
                </div>
              </button>

              {expandedSections.exchange && (
                <div className="border-t border-gray-200 p-4 space-y-2">
                  {pricing.exchangeItems.map((item, idx) => {
                    const isAccepted = item.customerAccepted === true;

                    return (
                      <div
                        key={idx}
                        className={`p-2 rounded border ${
                          isAccepted
                            ? "bg-purple-50 border-purple-200"
                            : "bg-gray-50 border-gray-300 opacity-70"
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="text-sm font-medium">
                                {item.description || `Item ${idx + 1}`}
                              </p>
                              {/* 🔥 FIX: Show acceptance status badge */}
                              {isAccepted ? (
                                <Badge variant="success" size="sm">
                                  <FiCheck className="w-3 h-3 mr-1" />
                                  Accepted
                                </Badge>
                              ) : (
                                <Badge variant="warning" size="sm">
                                  <FiClock className="w-3 h-3 mr-1" />
                                  Pending
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-gray-600">
                              {item.metalDetails?.purity} • {item.netWeight}g
                            </p>
                            {/* 🔥 FIX: Show warning for pending items */}
                            {!isAccepted && (
                              <p className="text-xs text-yellow-700 mt-1 flex items-center gap-1">
                                <FiAlertCircle className="w-3 h-3" />
                                Not deducted - awaiting customer acceptance
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            <span
                              className={`text-sm font-semibold ${
                                isAccepted ? "text-purple-700" : "text-gray-500"
                              }`}
                            >
                              {formatCurrency(
                                item.agreedValue || item.finalValue
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {/* 🔥 NEW: Summary of accepted vs pending */}
                  {pricing.exchangeItems.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-300">
                      <div className="flex justify-between text-xs text-gray-600 mb-1">
                        <span>
                          {
                            pricing.exchangeItems.filter(
                              (i) => i.customerAccepted
                            ).length
                          }{" "}
                          of {pricing.exchangeItems.length} accepted
                        </span>
                        <span>Only accepted items deducted</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Summary Totals */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            {/* Base Subtotal */}
            <SummaryLine
              label="Subtotal (Before Adjustments)"
              value={pricing.subtotal + (pricing.totalAdjustments || 0)}
              className="text-gray-700"
            />

            {/* Item-Level Adjustments */}
            {pricing.itemAdjustmentsTotal > 0 && (
              <div className="pl-4 space-y-1">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-orange-600 flex items-center gap-1">
                    <FiTag className="w-3 h-3" />
                    Item Discounts
                  </span>
                  <span className="text-orange-600 font-medium">
                    -{formatCurrency(pricing.itemAdjustmentsTotal)}
                  </span>
                </div>

                {/* Show which items had adjustments */}
                {pricing.appliedItemAdjustments?.map((item, idx) => (
                  <div key={idx} className="pl-4 text-xs text-gray-600">
                    <div className="flex justify-between">
                      <span>• {item.productName}</span>
                      <span>
                        {item.adjustments
                          .map((adj) => adj.description)
                          .join(", ")}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Cart-Level Adjustments */}
            {pricing.cartAdjustmentsTotal > 0 && (
              <div className="pl-4 space-y-1">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-orange-600 flex items-center gap-1">
                    <FiPercent className="w-3 h-3" />
                    Cart Discounts
                  </span>
                  <span className="text-orange-600 font-medium">
                    -{formatCurrency(pricing.cartAdjustmentsTotal)}
                  </span>
                </div>

                {/* Show cart adjustment details */}
                {pricing.appliedCartAdjustments?.map((adj, idx) => (
                  <div key={idx} className="pl-4 text-xs text-gray-600">
                    • {adj.description}
                  </div>
                ))}
              </div>
            )}

            {/* Existing Adjustments (Cart/Session) */}
            {pricing.existingAdjustmentsTotal > 0 && (
              <SummaryLine
                label="Cart/Session Adjustments"
                value={-pricing.existingAdjustmentsTotal}
                className="text-green-600 text-sm pl-4"
              />
            )}

            {/* Adjusted Subtotal */}
            {(pricing.totalAdjustments || 0) > 0 && (
              <div className="pt-1 border-t border-gray-300">
                <SummaryLine
                  label="Adjusted Subtotal"
                  value={pricing.subtotal}
                  className="text-gray-800 font-medium"
                />
              </div>
            )}

            <SummaryLine
              label={`GST (${pricing.gstRate || 3}%)`}
              value={pricing.totalGST}
              className="text-gray-700"
            />

            <div className="pt-2 border-t border-gray-300">
              <SummaryLine
                label="Grand Total"
                value={pricing.grandTotal}
                className="font-semibold text-lg text-gray-900"
              />
            </div>

            {/* 🔥 FIXED: Exchange Credit Display with Refund Handling */}
            {hasExchange && (
              <>
                {/* Show accepted exchange value */}
                {(pricing.totalExchangeValue || 0) > 0 && (
                  <SummaryLine
                    label="Exchange Credit (Accepted)"
                    value={-pricing.totalExchangeValue}
                    className="text-purple-600"
                  />
                )}

                {/* Show pending exchange value separately */}
                {pendingExchangeValue > 0 && (
                  <div className="flex justify-between items-center text-gray-400 text-sm">
                    <span className="flex items-center gap-1">
                      <FiClock className="w-3 h-3" />
                      Exchange Credit (Pending)
                    </span>
                    <span className="line-through">
                      -{formatCurrency(pendingExchangeValue)}
                    </span>
                  </div>
                )}

                {/* 🔥 NEW: Determine if payment or refund scenario */}
                {(() => {
                  const grandTotal = pricing.grandTotal || 0;
                  const exchangeValue = pricing.totalExchangeValue || 0;
                  const netAmount = grandTotal - exchangeValue;

                  // Scenario 1: Customer needs to pay (Products > Exchange)
                  if (netAmount > 0) {
                    return (
                      <div className="pt-2 border-t border-gray-300 border-2 bg-primary rounded-md bg-opacity-5 -mx-4 px-4 py-2 ">
                        <SummaryLine
                          label="💰 Net Payable"
                          value={netAmount}
                          className="font-bold text-xl text-text-on-primary"
                        />
                        <p className="text-xs text-text-on-primary mt-1">
                          Customer pays this amount
                        </p>
                      </div>
                    );
                  }

                  // Scenario 2: Customer receives refund (Exchange > Products)
                  else if (netAmount < 0) {
                    return (
                      <div className="pt-2 border-t border-gray-300 bg-green-50 border border-green-200 -mx-4 px-4 py-2 rounded">
                        <SummaryLine
                          label="💵 Amount to Refund"
                          value={Math.abs(netAmount)}
                          className="font-bold text-xl text-green-700"
                        />
                        <p className="text-xs text-green-700 mt-1">
                          Customer receives this amount back
                        </p>
                      </div>
                    );
                  }

                  // Scenario 3: Exactly balanced (Exchange = Products)
                  else {
                    return (
                      <div className="pt-2 border-t border-gray-300 bg-blue-50 border border-blue-200 -mx-4 px-4 py-2 rounded">
                        <div className="flex items-center justify-center gap-2">
                          <span className="text-lg font-bold text-blue-700">
                            ⚖️ Transaction Balanced
                          </span>
                        </div>
                        <p className="text-xs text-blue-700 text-center mt-1">
                          Exchange value equals purchase amount
                        </p>
                      </div>
                    );
                  }
                })()}
              </>
            )}
          </div>

          {/* Recalculate Button */}
          {!isLocked && onRecalculate && (
            <Button
              type="button"
              variant="outline"
              onClick={onRecalculate}
              disabled={calculating}
              className="w-full"
            >
              <FiRefreshCw
                className={`mr-2 ${calculating ? "animate-spin" : ""}`}
              />
              {calculating ? "Recalculating..." : "Recalculate Pricing"}
            </Button>
          )}

          {/* Last Updated */}
          {pricing.calculatedAt && (
            <div className="text-center">
              <p className="text-xs text-gray-500 flex items-center justify-center gap-1">
                <FiClock className="w-3 h-3" />
                Last updated: {formatDate(pricing.calculatedAt)}
              </p>
            </div>
          )}

          {/* Session Info */}
          {sessionPricing && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-2">
                <FiInfo className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-blue-700">
                  Pricing calculated from consultation session. Changes to items
                  or adjustments may require recalculation.
                </p>
              </div>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

// Item Pricing Row Component (Already fixed - keeping as is)
const ItemPricingRow = ({ item }) => {
  const [expanded, setExpanded] = useState(false);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  // CRITICAL FIX: Handle nested pricing structure
  let pricing;
  let breakdown = {};

  if (item.pricingSnapshot?.breakdown) {
    pricing = item.pricingSnapshot;
    breakdown = pricing.breakdown;
  } else if (item.pricingSnapshot?.branches) {
    const branchCode = Object.keys(item.pricingSnapshot.branches)[0];
    pricing = item.pricingSnapshot.branches[branchCode];
    breakdown = pricing?.breakdown || {};
  } else {
    pricing = item.finalPricing || item.originalPricing || {};
    breakdown = pricing?.breakdown || {};
  }

  const unitPrice = breakdown.finalAmount || breakdown.netPayable || 0;
  const totalPrice = unitPrice * (item.quantity || 1);

  return (
    <div className="bg-gray-50 rounded-lg p-3">
      <div className="flex justify-between items-start mb-2">
        <div className="flex-1">
          <p className="font-medium text-gray-900 text-sm">
            {item.productName || item.name || "Product"}
          </p>
          <p className="text-xs text-gray-500">
            Qty: {item.quantity || 1} • {item.productId || item.sku || "N/A"}
          </p>
        </div>
        <div className="text-right">
          <p className="font-semibold text-gray-900">
            {formatCurrency(totalPrice)}
          </p>
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="text-xs text-primary hover:text-primary-dark"
          >
            {expanded ? "Hide" : "Show"} details
          </button>
        </div>
      </div>

      {expanded && (
        <div className="mt-2 pt-2 border-t border-gray-200 space-y-1 text-xs">
          <div className="flex justify-between text-gray-600">
            <span>Metal Value:</span>
            <span>{formatCurrency(breakdown.metalValue || 0)}</span>
          </div>

          <div className="flex justify-between text-gray-600">
            <span>Making Charges:</span>
            <span>
              {formatCurrency(
                breakdown.charges?.making || breakdown.makingCharges || 0
              )}
            </span>
          </div>

          {(breakdown.charges?.wastage || breakdown.wastageCharges || 0) >
            0 && (
            <div className="flex justify-between text-gray-600">
              <span>Wastage:</span>
              <span>
                {formatCurrency(
                  breakdown.charges?.wastage || breakdown.wastageCharges || 0
                )}
              </span>
            </div>
          )}

          {(breakdown.charges?.hallmark || breakdown.hallmarkCharges || 0) >
            0 && (
            <div className="flex justify-between text-gray-600">
              <span>Hallmark:</span>
              <span>
                {formatCurrency(
                  breakdown.charges?.hallmark || breakdown.hallmarkCharges || 0
                )}
              </span>
            </div>
          )}

          {(breakdown.stoneCharges || 0) > 0 && (
            <div className="flex justify-between text-gray-600">
              <span>Stone Charges:</span>
              <span>{formatCurrency(breakdown.stoneCharges)}</span>
            </div>
          )}

          {(breakdown.discount || 0) > 0 && (
            <div className="flex justify-between text-green-600">
              <span>Discount:</span>
              <span>-{formatCurrency(breakdown.discount)}</span>
            </div>
          )}

          <div className="flex justify-between text-gray-700 font-medium pt-1 border-t">
            <span>Subtotal:</span>
            <span>{formatCurrency(breakdown.subtotal || 0)}</span>
          </div>

          <div className="flex justify-between text-gray-600">
            <span>GST ({breakdown.gst?.rate || 3}%):</span>
            <span>
              {formatCurrency(
                breakdown.gst?.amount || breakdown.gstAmount || 0
              )}
            </span>
          </div>

          <div className="flex justify-between text-gray-900 font-semibold pt-1 border-t">
            <span>Unit Price:</span>
            <span>{formatCurrency(unitPrice)}</span>
          </div>

          {(item.quantity || 1) > 1 && (
            <div className="flex justify-between text-primary font-bold pt-1 border-t">
              <span>Total ({item.quantity}x):</span>
              <span>{formatCurrency(totalPrice)}</span>
            </div>
          )}
        </div>
      )}

      {pricing?.rates && (
        <div className="mt-2 pt-2 border-t border-gray-200 text-xs text-gray-500">
          <div className="flex justify-between">
            <span>Rate (Sell):</span>
            <span>₹{pricing.rates.sell}/g</span>
          </div>
        </div>
      )}

      {item.adjustments && item.adjustments.length > 0 && (
        <div className="mt-2">
          <Badge variant="success" size="sm">
            {item.adjustments.length} adjustment(s) applied
          </Badge>
        </div>
      )}
    </div>
  );
};

// Adjustment Row Component (keeping as is)
const AdjustmentRow = ({ adjustment }) => {
  const formatValue = () => {
    if (adjustment.adjustment.type === "PERCENTAGE") {
      return `${adjustment.adjustment.value}% off`;
    } else if (adjustment.adjustment.type === "FIXED_AMOUNT") {
      return `₹${adjustment.adjustment.value.toLocaleString()} off`;
    } else if (adjustment.adjustment.type === "OVERRIDE_PRICE") {
      return `Override to ₹${adjustment.adjustment.overridePrice.toLocaleString()}`;
    }
    return "";
  };

  const getStatusColor = () => {
    if (adjustment.status === "ACTIVE") return "green";
    if (adjustment.status === "PENDING") return "yellow";
    return "gray";
  };

  return (
    <div className="flex items-center justify-between p-2 bg-green-50 rounded">
      <div className="flex items-center gap-2 flex-1">
        <FiPercent className="text-green-600 flex-shrink-0" />
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">
            {adjustment.adjustmentType?.replace(/_/g, " ") || "Adjustment"}
          </p>
          <p className="text-xs text-gray-600 truncate">{adjustment.reason}</p>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <span className="text-sm font-medium text-green-700">
          {formatValue()}
        </span>
        <Badge variant={getStatusColor()} size="sm">
          {adjustment.status}
        </Badge>
      </div>
    </div>
  );
};

// Summary Line Component (keeping as is)
const SummaryLine = ({ label, value, className = "" }) => (
  <div className={`flex justify-between items-center ${className}`}>
    <span>{label}:</span>
    <span>
      {value < 0 ? "-" : ""}₹{Math.abs(value || 0).toLocaleString()}
    </span>
  </div>
);

export default EnhancedPricingSummarySection;
